/* ==========================================================================
   Terms & Conditions Page Component
   Authentic 11-Section Legal Document for JALUD Embedded s.r.o.
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';

export function renderTermsConditionsPage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';
  const lang = getLocale() || 'en';
  const isCs = lang === 'cs';

  // Table of Contents Definitions
  const tocCs = [
    { id: 'terms-1', title: '1. Základní informace' },
    { id: 'terms-2', title: '2. Objednávka a uzavření smlouvy' },
    { id: 'terms-3', title: '3. Rozsah poskytovaného řešení' },
    { id: 'terms-4', title: '4. Softwarová licence' },
    { id: 'terms-5', title: '5. Cena a platební podmínky' },
    { id: 'terms-6', title: '6. Dodání a technická podpora' },
    { id: 'terms-7', title: '7. Reklamace a záruka' },
    { id: 'terms-8', title: '8. Charakter technologie' },
    { id: 'terms-9', title: '9. Ochrana soukromí' },
    { id: 'terms-10', title: '10. Odpovědnost' },
    { id: 'terms-11', title: '11. Závěrečná ustanovení' }
  ];

  const tocEn = [
    { id: 'terms-1', title: '1. Basic Information' },
    { id: 'terms-2', title: '2. Ordering and Contract Conclusion' },
    { id: 'terms-3', title: '3. Scope of Provided Solution' },
    { id: 'terms-4', title: '4. Software License' },
    { id: 'terms-5', title: '5. Pricing and Payment Terms' },
    { id: 'terms-6', title: '6. Delivery and Technical Support' },
    { id: 'terms-7', title: '7. Claims and Warranty' },
    { id: 'terms-8', title: '8. Nature of the Technology' },
    { id: 'terms-9', title: '9. Privacy and Data Protection' },
    { id: 'terms-10', title: '10. Liability' },
    { id: 'terms-11', title: '11. Final Provisions' }
  ];

  const currentToc = isCs ? tocCs : tocEn;

  container.innerHTML = `
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem;">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 860px; margin-bottom: 2rem;">
          <div class="sed-eyebrow">${isCs ? 'OBCHODNÍ A SMLUVNÍ PODMÍNKY' : 'LEGAL AGREEMENTS & POLICIES'}</div>
          <h1 class="sed-page-hero-title">${isCs ? 'Obchodní podmínky' : 'Terms & Conditions'}</h1>
          <p class="sed-section-desc">
            ${isCs 
              ? 'Základní pravidla pro poskytování a používání řešení Sound Event Detector (SED), včetně licencí, dodání, technické podpory a ochrany soukromí. Konkrétní podmínky každého projektu jsou vždy uvedeny v nabídce, objednávce nebo smlouvě.'
              : 'Core terms and rules for the provision and operation of Sound Event Detector (SED) solutions, including licensing, delivery, technical support, and data privacy. Specific terms for each deployment are governed by the respective quotation, purchase order, or agreement.'}
          </p>
        </div>
      </div>
    </section>

    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 5rem;">
      <div class="sed-container" style="max-width: 960px;">
        
        <!-- Company Metadata Header Card -->
        <div class="sed-card" style="padding: 1.75rem 2rem; margin-bottom: 2rem; background: #f8fafc; border: 1.5px solid var(--slate-line); border-radius: 12px; display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem;">
          <div>
            <div style="font-weight: 800; color: var(--navy-dark); font-size: 1.25rem; font-family: var(--font-heading); margin-bottom: 0.35rem;">
              JALUD Embedded s.r.o.
            </div>
            <div style="font-size: 0.92rem; color: var(--text-dark); margin-bottom: 0.3rem;">
              <strong>${isCs ? 'Poskytovatel technologie Sound Event Detector (SED)' : 'Provider of Sound Event Detector (SED) Technology'}</strong>
            </div>
            <div style="font-size: 0.88rem; color: var(--text-muted); line-height: 1.6;">
              ${isCs ? 'IČO: 03056287' : 'Company ID (IČO): 03056287'}<br>
              ${isCs ? 'Sídlo: Nepomucká 1355/261, Černice, 326 00 Plzeň' : 'Registered Office: Nepomucká 1355/261, Černice, 326 00 Plzeň, Czech Republic'}<br>
              E-mail: <a href="mailto:info@jalud-embedded.com" style="color: var(--blue-primary); text-decoration: none; font-weight: 600;">info@jalud-embedded.com</a>
            </div>
          </div>
          <div style="font-size: 0.82rem; font-weight: 700; color: var(--blue-primary); background: #ffffff; padding: 0.4rem 0.85rem; border-radius: 6px; border: 1px solid var(--slate-line);">
            ${isCs ? 'Verze 2026' : 'Version 2026'}
          </div>
        </div>

        <!-- Quick Table of Contents -->
        <div style="background: var(--blue-light); border: 1px solid var(--blue-primary); border-radius: 12px; padding: 1.5rem; margin-bottom: 2.5rem;">
          <div style="font-size: 0.85rem; font-weight: 800; color: var(--navy-dark); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem;">
            📑 ${isCs ? 'Obsah obchodních podmínek' : 'Table of Contents'}
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 0.6rem; font-size: 0.92rem;">
            ${currentToc.map((item, idx) => `
              <a href="#${item.id}" class="sed-terms-nav" style="color: var(--navy-dark); font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; gap: 8px; transition: color 0.2s;" onmouseover="this.style.color='var(--blue-primary)'" onmouseout="this.style.color='var(--navy-dark)'">
                <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.78rem; font-weight: 800; flex-shrink: 0;">${idx + 1}</span>
                <span>${item.title.replace(/^\d+\.\s*/, '')}</span>
              </a>
            `).join('')}
          </div>
        </div>

        <!-- Full 11 Sections -->
        <div class="sed-card" style="padding: 3rem; line-height: 1.8; color: var(--text-dark); background: #ffffff; border: 1.5px solid var(--slate-line); border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.03);">

          <!-- Section 1 -->
          <div id="terms-1" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">1</span>
              <span>${isCs ? 'Základní informace' : 'Basic Information'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Tyto podmínky shrnují základní pravidla pro poskytování a používání řešení Sound Event Detector (SED) společnosti JALUD Embedded s.r.o.'
                : 'These Terms summarize the core rules for the provision and utilization of Sound Event Detector (SED) solutions by JALUD Embedded s.r.o.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Naše řešení jsou určena především firmám, městům, veřejným institucím, distributorům a systémovým integrátorům.'
                : 'Our solutions are primarily designed for commercial enterprises, municipalities, public institutions, distributors, and system integrators.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Konkrétní rozsah dodávky, cena, délka licence, technická podpora a další podmínky jsou vždy uvedeny v nabídce, objednávce nebo smlouvě. Individuální dohoda má přednost před obecnými informacemi uvedenými na této stránce.'
                : 'The specific scope of supply, pricing, license duration, technical support, and other conditions are always specified in the formal quotation, purchase order, or contract. Individual agreements take precedence over the general information provided on this page.'}
            </p>
          </div>

          <!-- Section 2 -->
          <div id="terms-2" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">2</span>
              <span>${isCs ? 'Objednávka a uzavření smlouvy' : 'Ordering and Contract Conclusion'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Odeslání poptávky, žádosti o konzultaci nebo kontaktního formuláře na webu není závaznou objednávkou.'
                : 'Submitting an inquiry, consultation request, or web contact form does not constitute a binding purchase order.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Smluvní vztah vzniká až potvrzením objednávky společností JALUD Embedded s.r.o. nebo podpisem smlouvy mezi oběma stranami.'
                : 'A contractual relationship is established only upon formal written confirmation of the purchase order by JALUD Embedded s.r.o. or upon the bilateral signing of an agreement.'}
            </p>
          </div>

          <!-- Section 3 -->
          <div id="terms-3" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">3</span>
              <span>${isCs ? 'Rozsah poskytovaného řešení' : 'Scope of Provided Solution'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'SED může být poskytován jako softwarová aplikace pro kompatibilní zařízení, serverové řešení, mobilní aplikace, integrace do systémů třetích stran nebo společně s doplňkovým hardwarem.'
                : 'SED may be delivered as an embedded edge software application for compatible devices (smart cameras), a centralized server solution, a mobile application, integrations into third-party VMS/PSIM systems, or bundled with supplementary hardware.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Rozsah funkcí, podporované platformy, počet licencí, typy detekovaných zvukových událostí a způsob integrace jsou stanoveny podle konkrétního projektu.'
                : 'The functional scope, supported hardware platforms, license quantity, detected sound event classes, and integration methods are defined specifically for each deployment project.'}
            </p>
          </div>

          <!-- Section 4 -->
          <div id="terms-4" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">4</span>
              <span>${isCs ? 'Softwarová licence' : 'Software License'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Zakoupením licence získává zákazník právo používat software v dohodnutém rozsahu a po sjednanou dobu.'
                : 'By acquiring a software license, the customer receives the right to use the software within the agreed scope and for the agreed duration.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Software, algoritmy, dokumentace a další související technologie zůstávají duševním vlastnictvím společnosti JALUD Embedded s.r.o. nebo příslušných partnerů.'
                : 'All software, neural network algorithms, documentation, and associated technology remain the proprietary intellectual property of JALUD Embedded s.r.o. or its respective licensors.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Bez předchozího souhlasu není dovoleno software kopírovat, dále prodávat, poskytovat třetím osobám ani do něj neoprávněně zasahovat.'
                : 'Without prior written consent, the customer may not copy, sublicense, resell, redistribute to third parties, reverse-engineer, or tamper with the software.'}
            </p>
          </div>

          <!-- Section 5 -->
          <div id="terms-5" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">5</span>
              <span>${isCs ? 'Cena a platební podmínky' : 'Pricing and Payment Terms'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Cena řešení, licence, implementace a technické podpory je uvedena v konkrétní nabídce, objednávce nebo smlouvě.'
                : 'The pricing for solutions, licenses, deployment engineering, and ongoing support is detailed in the specific quotation, order confirmation, or contract.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Faktury jsou splatné ve lhůtě uvedené na příslušném dokladu.'
                : 'Invoices are payable within the maturity period explicitly indicated on each tax document.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Zkušební provoz, možnost vrácení peněz nebo jiné zvýhodněné podmínky platí pouze tehdy, pokud byly předem písemně dohodnuty.'
                : 'Pilot trial periods, money-back arrangements, or special evaluation terms are valid only when agreed upon in advance in writing.'}
            </p>
          </div>

          <!-- Section 6 -->
          <div id="terms-6" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">6</span>
              <span>${isCs ? 'Dodání a technická podpora' : 'Delivery and Technical Support'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Zákazník poskytne potřebnou součinnost pro instalaci, konfiguraci a testování řešení.'
                : 'The customer provides reasonable cooperation required for the installation, parameter tuning, network integration, and testing of the solution.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Technická podpora, aktualizace a garantované reakční doby jsou poskytovány v rozsahu stanoveném ve smlouvě nebo servisním ujednání SLA.'
                : 'Technical support, maintenance updates, and guaranteed response times are provided in accordance with the executed contract or Service Level Agreement (SLA).'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Pro správné fungování je nutné používat podporovaná zařízení, aktuální software a doporučenou konfiguraci.'
                : 'For reliable operation, systems must be operated on certified compatible hardware, run up-to-date firmware, and follow manufacturer deployment guidelines.'}
            </p>
          </div>

          <!-- Section 7 -->
          <div id="terms-7" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">7</span>
              <span>${isCs ? 'Reklamace a záruka' : 'Claims and Warranty'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Pokud řešení neodpovídá dohodnutým vlastnostem, zákazník oznámí problém bez zbytečného odkladu a poskytne informace potřebné k jeho ověření.'
                : 'If a delivered product does not match agreed specifications, the customer shall notify the provider without undue delay and supply relevant diagnostic data.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Záruka na případně dodaný hardware a podmínky odstranění vad softwaru jsou uvedeny v nabídce, smlouvě nebo záruční dokumentaci.'
                : 'Warranties for supplied hardware and software defect remedy conditions are stipulated in the quotation, sales agreement, or warranty policy.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Záruka se nevztahuje na problémy způsobené neodborným zásahem, nesprávnou instalací, nepodporovaným zařízením nebo službou třetí strany.'
                : 'Warranty claims exclude defects resulting from unauthorized intervention, improper physical installation, uncertified third-party devices, or external network outages.'}
            </p>
          </div>

          <!-- Section 8 -->
          <div id="terms-8" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">8</span>
              <span>${isCs ? 'Charakter technologie' : 'Nature of the Technology'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'SED využívá umělou inteligenci k rozpoznávání definovaných zvukových událostí.'
                : 'SED employs artificial intelligence and acoustic neural networks to classify defined sound events in real time.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Výsledky mohou být ovlivněny okolním prostředím, kvalitou zvuku, umístěním zařízení a jeho konfigurací.'
                : 'Detection efficacy may be affected by ambient acoustic environment, background noise levels, sensor positioning, and configuration.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Nelze proto zaručit zachycení každé události ani úplně vyloučit falešná upozornění.'
                : 'Consequently, the provider cannot guarantee detection of every possible event nor entirely rule out occasional false alarms.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'SED představuje doplňkovou vrstvu bezpečnostního systému a nenahrazuje lidské vyhodnocení ani další povinná bezpečnostní opatření.'
                : 'SED represents an assistive supplementary layer within an overall security infrastructure and does not replace human verification or statutory safety measures.'}
            </p>
          </div>

          <!-- Section 9 -->
          <div id="terms-9" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">9</span>
              <span>${isCs ? 'Ochrana soukromí' : 'Privacy and Data Protection'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'SED není určen k nepřetržitému nahrávání rozhovorů.'
                : 'SED is strictly not designed or intended for continuous recording or eavesdropping of conversations.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Při rozpoznání události pracuje s provozními metadaty a podle zvoleného nastavení může zpracovat krátký anonymizovaný zvukový vzorek související s danou událostí.'
                : 'Upon event classification, the system processes operational metadata and, depending on system settings, may process a short anonymized audio verification sample directly related to the incident.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Konkrétní způsob zpracování dat závisí na typu nasazení a nastavení systému.'
                : 'The specific method of data handling depends on deployment architecture and operational configuration.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Podrobnosti jsou uvedeny v technické dokumentaci a v samostatných <a href="#privacy-policy" data-route="privacy-policy" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline;">Zásadách ochrany osobních údajů</a>.'
                : 'Detailed provisions are outlined in the technical documentation and in our dedicated <a href="#privacy-policy" data-route="privacy-policy" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline;">Privacy Policy</a>.'}
            </p>
          </div>

          <!-- Section 10 -->
          <div id="terms-10" style="scroll-margin-top: 100px; margin-bottom: 2.5rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">10</span>
              <span>${isCs ? 'Odpovědnost' : 'Liability'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Společnost JALUD Embedded s.r.o. neodpovídá za problémy způsobené použitím řešení v rozporu s dokumentací, neodborným zásahem, výpadkem připojení, poruchou zařízení třetí strany nebo nesprávným vyhodnocením upozornění zákazníkem.'
                : 'JALUD Embedded s.r.o. shall not be liable for damages resulting from operation contrary to documentation, improper tampering, third-party network outages, hardware malfunctions of non-certified systems, or incorrect incident assessments by the customer.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Zákazník odpovídá za nastavení vlastních bezpečnostních postupů a za způsob reakce na detekované události.'
                : 'The customer remains solely responsible for establishing their internal security dispatch protocols and operational reactions to detected alerts.'}
            </p>
          </div>

          <!-- Section 11 -->
          <div id="terms-11" style="scroll-margin-top: 100px; margin-bottom: 1rem;">
            <h3 style="font-family: var(--font-heading); font-size: 1.35rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.85rem; border-bottom: 2px solid var(--blue-light); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">11</span>
              <span>${isCs ? 'Závěrečná ustanovení' : 'Final Provisions'}</span>
            </h3>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Smluvní vztahy se řídí právem České republiky.'
                : 'All contractual relationships are governed by the laws of the Czech Republic and applicable EU directives.'}
            </p>
            <p style="margin-bottom: 0.75rem; font-size: 1.02rem;">
              ${isCs 
                ? 'Pokud se některé ustanovení těchto podmínek liší od uzavřené smlouvy nebo potvrzené objednávky, má přednost individuální dohoda.'
                : 'In the event of discrepancies between these general conditions and an executed bilateral contract or accepted order, the specific agreement shall prevail.'}
            </p>
            <p style="font-size: 1.02rem;">
              ${isCs 
                ? 'Dotazy k obchodním podmínkám je možné zaslat na adresu <a href="mailto:info@jalud-embedded.com" style="color: var(--blue-primary); font-weight: 700; text-decoration: none;">info@jalud-embedded.com</a>.'
                : 'Questions regarding terms and commercial conditions may be directed to <a href="mailto:info@jalud-embedded.com" style="color: var(--blue-primary); font-weight: 700; text-decoration: none;">info@jalud-embedded.com</a>.'}
            </p>
          </div>

        </div>
      </div>
    </section>
  `;

  // Attach smooth scrolling for terms navigation links
  container.querySelectorAll('.sed-terms-nav').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = link.getAttribute('href').replace('#', '');
      const targetEl = container.querySelector(`#${targetId}`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });

  // Attach data-route links (e.g. Privacy Policy)
  container.querySelectorAll('[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const route = link.getAttribute('data-route');
      if (typeof onNavigate === 'function') {
        onNavigate(route);
      } else {
        window.location.hash = route;
      }
    });
  });

  return container;
}
